import React from 'react'

import * as c from '../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../common/helpers/totalCostDisplay'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import * as gs from '../../../app/gStyles'

import {
  securityContract,
  securityContractLast,
  securityContractPreSignatureA,
  securityContractPreSignatureB,
  securityContractPreSignatureC,
} from '../../constants'

import ContractLogo, { useContractWatermark } from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './SecurityContract.module.css'
import { DetailHeader, DetailLine } from './Detail'

export type WildcatContractProps = c.r<{
  customerID: string
  isPDF: boolean
  readOnly?: boolean
  toggleSecuritySignatureModalCompany(): void
  toggleSecuritySignatureModalCustomer(): void
}>

export default React.memo<WildcatContractProps>(function WildcatContract({
  customerID,
  isPDF,
  readOnly,
  toggleSecuritySignatureModalCompany,
  toggleSecuritySignatureModalCustomer,
}) {
  //#region styling
  //#endregion
  //#region global
  const { contractCompanyFull } = r.useContractCompany(customerID)

  const totalCost = r.useTotalCost('customer', customerID)

  const { appliedLoan } = r.usePayment(customerID)

  const monthlyPayment = React.useMemo((): number => {
    if (!appliedLoan) return 0
    const { grandTotalWDF, paymentMethod, termInterest, termYears } =
      appliedLoan
    // Cash customers have no loan payment, and their stored termYears may
    // be 0 (division by zero → Infinity) or stale from a prior financing
    // selection — mirror Payment.tsx, which only shows $X/mo for financing.
    if (paymentMethod === 'cash' || termYears <= 0) return 0
    if (termInterest === 0) {
      return grandTotalWDF / termYears / 12
    }
    return c.calcLoanMonthlyPay(grandTotalWDF, termInterest * 100, termYears)
  }, [appliedLoan])

  const [customerData] = r.useCustomer(customerID)
  const totalPending = totalCostPendingApply(customerData, c.IS_MAIN)

  const {
    customerAddress,
    customerEmail,
    customerName,
    customerPhone,
    homeRep,

    security_contract_sign_company,
    security_contract_sign_company_date,
    security_contract_sign_customer,
    security_contract_sign_customer_date,
    security_monitoring_enabled,
    solarCompany,
  } = customerData

  // No watermark once anything is signed here (V67).
  const watermark = useContractWatermark(
    customerID,
    !!(security_contract_sign_customer || security_contract_sign_company),
  )

  const {
    collapsed,
    collapsedHandleMax,
    collapsedHandleMin,
    detailLines: _detailLines,
  } = r.useDetailLines(customerID)

  // Same defect and same fix as FirstElectricContract — see the comment there.
  // Guarding on `length > 1` dropped BOTH halves when the scope resolved to
  // exactly one line, printing a SCOPE OF WORK with headings, no rows and a
  // TOTAL. `slice(0, -1)` is already [] at length 1 and 0.
  const detailLines = _detailLines.slice(0, -1)
  const lastDetailLine = _detailLines[_detailLines.length - 1]

  const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
    customerID,
    'security_contract_co_buyer',
  )
  //#endregion
  //#region local

  //#endregion

  const buttonSignatureStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': { backgroundColor: c.companyColors[solarCompany] },
      width: 256,
    }),
    [solarCompany],
  )

  return (
    <ReportPages
      companyBorder
      customerID={customerID}
      hPad
      watermark={watermark}
    >
      <div style={sx.logo}>
        <ContractLogo isPDF={isPDF} readOnly={readOnly} size="m" />
      </div>

      {gapBig}

      <div>
        <p className={styles['heading']}>{contractCompanyFull.longName}</p>

        <p style={sx.companyInfo}>{contractCompanyFull.address1}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.address2}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.email}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.phoneNumber}</p>
      </div>

      {gapMedium}

      <p style={sx.node}>
        This is a Purchase Agreement ("PA") on this Date{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {security_contract_sign_customer || security_contract_sign_company
            ? c.formatContractDate(
                security_contract_sign_customer_date ||
                  security_contract_sign_company_date,
                solarCompany,
              )
            : c.formatContractDate(Date.now(), solarCompany)}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        between {contractCompanyFull.longName} ("Company") and:
      </p>

      {gapMedium}

      <p style={sx.node}>
        Buyer:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerName}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Co-Buyer:{' '}
        <input
          className={styles['input-inline-wide']}
          disabled={
            readOnly ||
            !!security_contract_sign_customer ||
            !!security_contract_sign_company
          }
          onChange={coBuyerHandle}
          title="Co-Buyer"
          value={coBuyer}
        />
      </p>

      <p style={sx.node}>
        Property Address:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerAddress}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      <p style={sx.node}>
        Home / Cell Home:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerPhone}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        E-mail:
        <span className={styles['underlined']}>
          {c.blankChar(8)}
          {customerEmail}
          {c.blankChar(8)}
        </span>
      </p>

      <p style={sx.node}>
        sometimes collectively referred to herein as the "Parties or
        individually a "Party".
      </p>

      {gapSmall}

      <p style={sx.node}>
        Rep Name:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {c.displayHomeRepOnContract(homeRep)}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Rep Phone:
        <span className={styles['underlined']}>
          {c.blankChar(8)}
          n/a
          {c.blankChar(8)}
        </span>
      </p>

      {gapMedium}

      <p className={styles['subheading']}>SCOPE OF WORK</p>

      {gapMedium}

      <DetailHeader isPDF={isPDF} />

      {detailLines.map(({ id, name, notes, price, qty }) => (
        <DetailLine
          collapsed={!!collapsed[id]}
          id={id}
          isPDF={isPDF}
          name={name}
          notes={notes}
          onCollapse={collapsedHandleMin[id]!}
          onExpand={collapsedHandleMax[id]!}
          price={price}
          qty={qty}
          readOnly={readOnly}
        />
      ))}

      <div style={gs.flowRootWidth100}>
        {lastDetailLine && (
          <DetailLine
            collapsed={!!collapsed[lastDetailLine.id]}
            id={lastDetailLine.id}
            isPDF={isPDF}
            name={lastDetailLine.name}
            notes={lastDetailLine.notes}
            price={lastDetailLine.price}
            qty={lastDetailLine.qty}
            onCollapse={collapsedHandleMin[lastDetailLine.id]!}
            onExpand={collapsedHandleMax[lastDetailLine.id]!}
            readOnly={readOnly}
          />
        )}

        {gapSmall}

        <p style={{ ...sx.node, textAlign: 'end' }}>
          <span style={sx.bold}>
            TOTAL: {c.blankChar(12)}
            {totalPending
              ? TOTAL_PENDING_LABEL
              : Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  style: 'currency',
                }).format(totalCost)}
          </span>
        </p>

        {monthlyPayment > 0 && (
          <p style={{ ...sx.node, textAlign: 'end' }}>
            Monthly Payment: {c.blankChar(4)}
            {Intl.NumberFormat('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            }).format(monthlyPayment)}
            /mo
          </p>
        )}

        {security_monitoring_enabled === 'yes' && (
          <p style={{ ...sx.node, textAlign: 'end' }}>
            Monitoring service: $9.99/mo (billed monthly, not included in total)
          </p>
        )}

        {gapBig}

        <p style={sx.boldCursive}>
          Disclaimer: If cost to complete work is more than approved financed
          amount then customer is responsible for remaining balance.
        </p>
      </div>
      {/* end flow-root */}

      {gapBig}

      <div style={gs.flowRootWidth100}>
        <p className={styles['subheading']}>CUSTOMER/OWNER'S RIGHT TO CANCEL</p>

        {gapMedium}

        <p style={sx.node}>
          If this Agreement was signed at a residence and you do not want the
          materials or services, you as the owner of the Property, may cancel
          this Agreement at any time prior to midnight of the third day after
          the date of this transaction, by providing written notice to the
          Company.
        </p>
      </div>

      {gapMedium}

      <div style={gs.flowRootWidth100}>
        <p className={styles['subheading']}>
          TERMS AND CONDITIONS OF SERVICE AGREEMENT
        </p>

        {gapMedium}

        <p style={sx.node}>
          <span style={sx.bold}>1. {securityContract[0]!.title}:</span>{' '}
          {securityContract[0]!.body}
        </p>

        {gapMedium}
      </div>

      {securityContract.slice(1).map(({ body, title }, i) => (
        <React.Fragment key={i}>
          <p style={sx.node}>
            <span style={sx.bold}>
              {i + 2}. {title}:
            </span>{' '}
            {body}
          </p>

          {gapMedium}
        </React.Fragment>
      ))}

      {/* This flow-root ensures signatures aren't awkwardly by themselves on
        a lone page, tying them together with the last paragraph of 
        conditions */}
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>
          <span style={sx.bold}>
            {securityContract.length + 1}. {securityContractLast.title}:
          </span>{' '}
          {securityContractLast.body}
        </p>

        {gapMedium}

        <p style={sx.node}>{securityContractPreSignatureA}</p>

        {gapMedium}

        <p style={sx.node}>{securityContractPreSignatureB}</p>

        {gapSmall}

        <p style={sx.node}>{securityContractPreSignatureC}</p>

        <div className={styles['signatures']}>
          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Property owner:</p>

              <span>{customerName}</span>
            </div>

            {gs.rowGap24}

            {security_contract_sign_customer && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={security_contract_sign_customer}
                  width={200}
                />
                <p>
                  {c.formatContractDate(
                    security_contract_sign_customer_date,
                    solarCompany,
                    'MM/DD/YYYY - hh:mma',
                  )}
                </p>
              </>
            )}

            <p>Address: {customerAddress}</p>

            {gs.rowGap24}

            {isPDF || security_contract_sign_customer ? (
              <div />
            ) : (
              <Button
                data-guided-signature="security_contract_sign_customer"
                onClick={toggleSecuritySignatureModalCustomer}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Client Signature
              </Button>
            )}
          </div>

          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Contractor:</p>
              <span>{c.displayHomeRepOnContract(homeRep)}</span>
            </div>

            {security_contract_sign_company && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={security_contract_sign_company}
                  width={200}
                />
                <p>
                  {c.formatContractDate(
                    security_contract_sign_company_date,
                    solarCompany,
                    'MM/DD/YYYY - hh:mma',
                  )}
                </p>
              </>
            )}

            {gs.rowGap24}

            <p className={styles['label-signature']}>
              Authorized Representative of
            </p>
            <p className={styles['label-signature']}>
              {contractCompanyFull.longName}
            </p>

            {gs.rowGap24}

            {isPDF || security_contract_sign_company ? (
              <div />
            ) : (
              <Button
                onClick={toggleSecuritySignatureModalCompany}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Authorized Signature
              </Button>
            )}
          </div>
        </div>
      </div>
    </ReportPages>
  )
})

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  margin: 0,
}
const sx = {
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'center' as const,
  },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: sxNode,
  bold: { ...sxNode, fontWeight: 'bold' },
  boldCursive: { ...sxNode, fontStyle: 'italic', fontWeight: 'bold' },
}
